import { Form, Formik, FormikProps } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { AppContext } from '../../../appContext/AppContext';
import { ConfirmDialog } from '../../../component/ConfirmDialog';
import { IKeVatTuRequest, IPhieuChiDinhVatTuItem } from '../../../phan-he-kham-benh/models/VatTuModels';
import { handleConvertBenhNhanInfo } from '../../../phan-he-noi-tru/common/functionCommon';
import { APP_TIMESTAMP_FORMAT, CODE, ORDER_TYPE_ID, REQUEST_FROM, REQUEST_TYPE, RESPONSE_MESSAGE } from '../../../utils/Constant';
import { formatDateDTO, formatDateTime } from '../../../utils/FormatUtils';
import { localStorageItem } from '../../../utils/LocalStorage';
import { usePhanHeDieuTriNoiTruContext } from '../../PhanHeDieuTriContext';
import { fetchKeThuoc, updateKeThuoc } from '../../services/DieuTriServices';
import VatTuHeader from './components/header-vat-tu';
import ThongTinKeVatTu from './components/thong-tin-ke-vat-tu';
import TreeVatTu from './components/tree-vat-tu';
import { useKeVatTuContext } from './context';
import './style.scss';
import { usePhanHeNoiTruContext } from '../../../phan-he-noi-tru/PhanHeNoiTruContext';
import { useIntl } from 'react-intl';
import { removeEventEnter } from '../../../utils/AppFunction';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';

type Props = {
	onClose: () => void;
	itemEdit?: any;
	onSave?: () => void;
};

const FormKeVatTu = ({ onClose, itemEdit, onSave }: Props) => {
	const intl = useIntl();
	const { benhNhanDieuTri } = usePhanHeDieuTriNoiTruContext();
	const { statusChangedEventFunc } = usePhanHeNoiTruContext();
	const { vatTuChecked, treeValue, deletedList, phieuDieuTri, setVatTuChecked, setIsEdit } = useKeVatTuContext();
	const { setIsLoading } = useContext(AppContext);
	const [donVatTuDetail, setDonVatTuDetail] = useState<any>();
	const [inventoryValidateMessage, setInventoryValidateMessage] = useState<string[] | null>(null);

	const formikRef = useRef<FormikProps<any>>(null);

	const userInfo = localStorageItem.get('current-user');
	const userMetaData = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA);
	const validationSchema = Yup.object().shape({
		treatmentSheet: Yup.object().nullable().required(intl.formatMessage({ id: "VALIDATION.REQUIRE" })),
	});
	const initialValues = {
		ngayYLenh: formatDateDTO(phieuDieuTri?.prescriptionDate) || "",
		thuTien: true,
		provisionalDiagnosis: benhNhanDieuTri?.icd10ProvisionalDiagnosisCode,
		provisionalDiagnosisName: benhNhanDieuTri?.icd10ProvisionalDiagnosisName,
		comorbidity: benhNhanDieuTri?.icd10ProvisionalComorbidity,
		treatmentSheet: phieuDieuTri || null,
	};

	const validateInventoryForAutoPrescriptions = (
		supplies: any[],
		autoPrescriptions: any[]
	): { isValid: boolean; errorMessages: string[] } => {
		const totalDays = 1 + (autoPrescriptions?.length || 0);
		const errorMessages: string[] = [];

		for (const supply of supplies) {
			const soLuong = supply.soLuong || 0;
			const tonKho = supply.tonKho || 0;
			const totalRequired = soLuong * totalDays;

			if (totalRequired > tonKho) {
				errorMessages.push(
					`[${supply.tenVatTu}] - SL cần: ${totalRequired} (${soLuong} × ${totalDays} ngày), SL khả dụng: ${tonKho}`
				);
			}
		}

		return {
			isValid: errorMessages.length === 0,
			errorMessages
		};
	};

	const handleSubmit = async (values: any) => {
		try {
			if (!vatTuChecked.filter(row => !row.deleted).length) {
				toast.warn('Chưa có vật tư được kê cho đơn này');
				return;
			}

			if (!values?.provisionalDiagnosisName && !values?.diagnosisNonCoded) {
				toast.warn('Chưa nhập thông tin chẩn đoán!');
				return;
			}

			const emptyQuantityRow = vatTuChecked.find(row => !row.soLuong);
			if (emptyQuantityRow) {
				toast.warn(
					`Vật tư với mã vật tư ${emptyQuantityRow.maVatTu} chưa nhập số lượng kê`
				);
				return;
			}

			// Validate số lượng khả dụng khi có kê tự động
			const autoPrescriptionsData = values?.autoPrescriptions || [];
			if (autoPrescriptionsData.length > 0) {
				const inventoryValidation = validateInventoryForAutoPrescriptions(
					vatTuChecked.filter(row => !row.deleted),
					autoPrescriptionsData
				);
				if (!inventoryValidation.isValid) {
					setInventoryValidateMessage(inventoryValidation.errorMessages);
					return;
				}
			}

			setIsLoading(true);

			const phieuChiDinhList: Partial<IPhieuChiDinhVatTuItem>[] = vatTuChecked.map(row => {
				return {
					...row,
					id: row.idBackend ?? null,
					termId: row.termId,
					visitId: benhNhanDieuTri?.visitId,
					patientId: benhNhanDieuTri?.patientId,
					orderId: row.orderId,
					orderTypeId: parseInt(ORDER_TYPE_ID.VAT_TU),
					medicalItemId: row.idVatTu,
					medicalItemCode: row.maVatTu,
					medicalItemName: row.tenVatTu,
					name: row.tenVatTu,
					unit: row.donVi,
					totalQuantity: row.soLuong,
					deleted: row.deleted,
					visitObjectTypeId: benhNhanDieuTri.visitObjectTypeId,
					visitObjectTypeName: benhNhanDieuTri.visitObjectTypeName,
					unitPrice: row.donGia,
					intoMoney: row.donGia * row.soLuong,
					bidId: row.bidId,
					serialNumber: row.serialNumber,
					consignment: row.consignment,
					percentIns: row.rateHealthInsurance || row.percentIns
				};
			});

			let autoPrescriptions = values?.autoPrescriptions?.map((item: any) => ({
				prescriptionDate: item?.value,
				treatmentSheetId: item?.treatmentSheetId,
			})) || [];

			const requestData: Partial<IKeVatTuRequest> & {
				treatmentSheetId: string,
				autoPrescriptions: any[],
				formCode: string,
				medicalItemTypeCode: string,
				visitObjectTypeCode: string,
				pid?: string,
			} = {
				id: donVatTuDetail?.id ?? null,
				encounterId: donVatTuDetail?.encounterId ?? null,
				treatmentSheetId: values?.treatmentSheet?.id ?? phieuDieuTri?.id,
				visitId: benhNhanDieuTri?.visitId,
				patientId: benhNhanDieuTri?.patientId,
				orderTypeId: parseInt(ORDER_TYPE_ID.VAT_TU),
				doctorOrderDay: values.ngayYLenh,
				treatmentDoctorUser: userInfo.sub,
				treatmentDoctorName: userInfo.name,
				designatedPerson: userInfo.name,
				designatedPlace: userInfo?.departments?.[0]?.name,
				designatedDate: formatDateTime(new Date(), APP_TIMESTAMP_FORMAT),
				comorbidity: values.comorbidity,
				provisionalDiagnosis: values.provisionalDiagnosis,
				provisionalDiagnosisName: values.provisionalDiagnosisName,
				diagnosisNonCoded: values.diagnosisNonCoded,
				warehouseId: treeValue.id,
				warehouseName: treeValue.name,
				warehouseType: treeValue?.warehouseType,
				lstMedicalItemOrder: phieuChiDinhList.concat(Array.from(deletedList.values())) as IPhieuChiDinhVatTuItem[],
				autoPrescriptions,
				formCode: REQUEST_FROM.DIEU_TRI,
				medicalItemTypeCode: REQUEST_TYPE.VAT_TU,
				visitObjectTypeCode: benhNhanDieuTri?.visitObjectTypeCode,
				pid: userMetaData?.personId,
			};

			const { data } = itemEdit ? await updateKeThuoc(requestData) : await fetchKeThuoc(requestData);
			if (CODE.SUCCESS === data?.code) {
				toast.success(
					donVatTuDetail?.id
						? RESPONSE_MESSAGE.UPDATE.SUCCESS
						: RESPONSE_MESSAGE.ADD.SUCCESS
				);
				statusChangedEventFunc();
				onSave && onSave();
				onClose();
			} else {
				toast.error(data?.[0]?.errorMessage || RESPONSE_MESSAGE.ERROR);
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const getDetail = (data: any) => {
		setDonVatTuDetail(data);
		formikRef.current?.setValues({
			...formikRef.current.initialValues,
			ngayYLenh: data.doctorOrderDay,
			provisionalDiagnosis: data.provisionalDiagnosis,
			provisionalDiagnosisName: data.provisionalDiagnosisName,
			comorbidity: data.comorbidity,
		});

		if (data.lstMedicalItemOrderRes?.length) {
			const listVatTuSelected = data.lstMedicalItemOrderRes.map(
				(row: any, index: number) => ({
					...row,
					idBackend: row.id,
					maVatTu: row.medicalItemCode,
					tenVatTu: row.name,
					idVatTu: row.medicalItemId,
					donVi: row.unit,
					soLuong: row.totalQuantity,
					donGia: row.unitPrice,
					thanhTien: row.intoMoney,
					doiTuong: row.visitObjectTypeName || benhNhanDieuTri?.visitObjectTypeName,
					index,
					servicePrice: row.svcFee,
					hospitalPrice: row.svcFeeHospital,
					bhytPrice: row.svcFeeIns,
				})
			);
			setVatTuChecked(listVatTuSelected);
		}
	}

	useEffect(() => {
		if (itemEdit) {
			getDetail(itemEdit);
			setIsEdit(true);
		}
	}, [itemEdit]);

	return (
		<Formik<any>
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			innerRef={formikRef}
		>
			<Form className="spaces h-calc-vh-50" onKeyDown={removeEventEnter}>
				<div className="h-100">
					<Modal.Header className="py-3 header-modal">
						<VatTuHeader onClose={onClose} benhNhanInfo={handleConvertBenhNhanInfo(benhNhanDieuTri)} />
					</Modal.Header>

					<Modal.Body className="px-0 py-6 spaces overflow-hidden h-calc-vh-90">
						<div className="d-flex h-100">
							<div className="spaces width-20 bg-white radius-2 d-flex flex-column min-w-200">
								<TreeVatTu donVatTuDetail={donVatTuDetail} disableSelectKhoVatTu={donVatTuDetail?.id || vatTuChecked.length} />
							</div>
							<div className="d-flex flex-column spaces width-80 bg-light radius-2">
								<ThongTinKeVatTu />
							</div>
						</div>
					</Modal.Body>
				</div>
				{inventoryValidateMessage && (
					<ConfirmDialog
						className="z-index-1060"
						show={Boolean(inventoryValidateMessage)}
						title="Thông báo"
						message={
							<div>
								<div>Số lượng vật tư kê tự động vượt quá số lượng khả dụng:</div>
								<div className="mt-2">
									{inventoryValidateMessage?.map((msg, index) => (
										<div key={index}>&#8226;&nbsp;{msg}</div>
									))}
								</div>
							</div>
						}
						yes="Đóng"
						onYesClick={() => setInventoryValidateMessage(null)}
						onCloseClick={() => setInventoryValidateMessage(null)}
					/>
				)}
			</Form>
		</Formik>
	);
};

export default FormKeVatTu;
